import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, qs, type IntegrationsStatus, type MessageDetail, type MessagePurpose, type MessageRow, type MessageStatus } from '../api';
import { Dialog, Empty, ErrorAlert, Loading } from '../components/ui';
import { dateTime } from '../format';

export const PURPOSE_LABEL: Record<MessagePurpose, string> = {
  confirmacao_agenda: 'Confirmação de agenda',
  lembrete: 'Lembrete',
  reagendamento: 'Reagendamento',
  cancelamento: 'Cancelamento',
  relatorio_sessao: 'Relatório da sessão',
  extrato: 'Extrato',
};

export const STATUS_LABEL: Record<MessageStatus, string> = {
  preparado: 'Preparada — aguardando conferência',
  em_processamento: 'Em processamento',
  aceito: 'Aceita pelo provedor',
  falhou: 'Falhou / não enviada',
  entregue: 'Entregue (comprovado)',
  preparado_aberto: 'Preparada/aberta no aplicativo (entrega não comprovada)',
  invalidado: 'Invalidada',
};

const STATUS_CLASS: Record<MessageStatus, string> = {
  preparado: 'badge-demo',
  em_processamento: 'badge-brand',
  aceito: 'badge-ok',
  falhou: 'badge-demo',
  entregue: 'badge-ok',
  preparado_aberto: 'badge-brand',
  invalidado: 'badge-off',
};

export const MessageStatusBadge = ({ status }: { status: MessageStatus }) => <span className={`badge ${STATUS_CLASS[status]}`}>{STATUS_LABEL[status]}</span>;

// Botão "preparar mensagem": cria a mensagem (sem enviar) e abre o compositor para conferência.
export function PrepareMessageButton({ label, body, className = 'btn btn-sm' }: { label: string; body: Record<string, unknown>; className?: string }) {
  const navigate = useNavigate();
  const m = useMutation({ mutationFn: () => api.post<{ id: string }>('/messages/prepare', body), onSuccess: (r) => navigate(`/mensagens/${r.id}`) });
  return (
    <>
      <button className={className} disabled={m.isPending} onClick={() => m.mutate()}>
        {m.isPending ? 'Preparando…' : label}
      </button>
      {m.error && <ErrorAlert error={m.error} />}
    </>
  );
}

export function RelatedMessages({ filter }: { filter: Record<string, string> }) {
  const q = useQuery({ queryKey: ['messages', filter], queryFn: () => api.get<{ data: MessageRow[] }>(`/messages${qs(filter)}`) });
  if (!q.data?.data.length) return null;
  return (
    <div className="list" style={{ marginTop: '0.75rem' }}>
      {q.data.data.map((m) => (
        <Link key={m.id} to={`/mensagens/${m.id}`} className="list-item">
          <div className="title">
            {PURPOSE_LABEL[m.purpose]} · {m.channel === 'email' ? 'E-mail' : 'WhatsApp'} <MessageStatusBadge status={m.status} />
          </div>
          <div className="meta">
            <span>{m.subject}</span>
            {m.lastSentAt && <span>Último envio/abertura: {dateTime(m.lastSentAt)}</span>}
          </div>
        </Link>
      ))}
    </div>
  );
}

export function MessagesList() {
  const [status, setStatus] = useState<'pendentes' | 'enviadas' | 'all'>('pendentes');
  const [scope, setScope] = useState<'mine' | 'all'>('all');
  const q = useQuery({ queryKey: ['messages', status, scope], queryFn: () => api.get<{ data: MessageRow[] }>(`/messages${qs({ status, scope })}`) });
  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Situação">
          <button aria-pressed={status === 'pendentes'} onClick={() => setStatus('pendentes')}>
            Pendentes
          </button>
          <button aria-pressed={status === 'enviadas'} onClick={() => setStatus('enviadas')}>
            Enviadas/abertas
          </button>
          <button aria-pressed={status === 'all'} onClick={() => setStatus('all')}>
            Todas
          </button>
        </div>
        <div className="segmented" role="group" aria-label="Escopo">
          <button aria-pressed={scope === 'all'} onClick={() => setScope('all')}>
            Toda a TECHNOVE
          </button>
          <button aria-pressed={scope === 'mine'} onClick={() => setScope('mine')}>
            Preparadas por mim
          </button>
        </div>
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : q.data?.data.length === 0 ? (
        <Empty>Nenhuma mensagem. Prepare mensagens a partir da agenda, da execução ou do extrato.</Empty>
      ) : (
        <div className="list">
          {q.data?.data.map((m) => (
            <Link key={m.id} to={`/mensagens/${m.id}`} className="list-item">
              <div className="title">
                {m.clientName} · {PURPOSE_LABEL[m.purpose]} <MessageStatusBadge status={m.status} />
              </div>
              <div className="meta">
                <span>{m.channel === 'email' ? 'E-mail' : 'WhatsApp'}</span>
                <span>{m.subject}</span>
                <span>Atualizada {dateTime(m.updatedAt)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ compositor (seção 15)

export function MessageComposerPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['message', id], queryFn: () => api.get<MessageDetail>(`/messages/${id}`) });
  const integrations = useQuery({ queryKey: ['integrations-status'], queryFn: () => api.get<IntegrationsStatus>('/integrations/status') });
  const [form, setForm] = useState<{ recipientIds: string[]; subject: string; body: string; channel: 'email' | 'whatsapp' } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [resend, setResend] = useState<{ lastSentAt: string; sendCount: number; sameContent: boolean } | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [copied, setCopied] = useState(false);
  const keyRef = useRef<string>(crypto.randomUUID());

  useEffect(() => {
    const m = q.data;
    if (m && !dirty) setForm({ recipientIds: m.recipients.map((r) => r.contactId), subject: m.subject, body: m.body, channel: m.channel });
  }, [q.data, dirty]);
  // Qualquer mudança no conteúdo exige nova conferência.
  useEffect(() => setReviewed(false), [q.data?.contentHash]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['message', id] });
    void qc.invalidateQueries({ queryKey: ['messages'] });
    void qc.invalidateQueries({ queryKey: ['me-overview'] });
  };
  const save = useMutation({
    mutationFn: () => api.put(`/messages/${id}`, { ...form, version: q.data!.version }),
    onSuccess: () => {
      setDirty(false);
      refresh();
    },
  });
  const refreshAttachments = useMutation({ mutationFn: () => api.post<{ changed: boolean }>(`/messages/${id}/refresh-attachments`), onSuccess: refresh });
  const send = useMutation({
    mutationFn: (confirmResend: boolean) => api.post<{ status: string; simulator?: boolean }>(`/messages/${id}/send`, { contentHash: q.data!.contentHash, idempotencyKey: keyRef.current, confirmResend }),
    onSuccess: () => {
      keyRef.current = crypto.randomUUID();
      setResend(null);
      refresh();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'resend_confirmation_required') setResend(e.details as { lastSentAt: string; sendCount: number; sameContent: boolean });
      if (e instanceof ApiError && e.code === 'email_not_configured') {
        setNotConfigured(true);
        keyRef.current = crypto.randomUUID();
        refresh();
      }
    },
  });
  const opened = useMutation({
    mutationFn: (via: 'whatsapp' | 'email_app') => api.post(`/messages/${id}/opened-externally`, { contentHash: q.data!.contentHash, idempotencyKey: crypto.randomUUID(), via }),
    onSuccess: refresh,
  });

  if (q.isLoading || !form) return <Loading />;
  if (q.error) return <ErrorAlert error={q.error} />;
  const m = q.data!;
  const editable = m.status !== 'invalidado' && m.status !== 'em_processamento';
  const set = <K extends keyof NonNullable<typeof form>>(k: K, v: NonNullable<typeof form>[K]) => {
    setDirty(true);
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };
  const blocking = dirty || m.problems.length > 0 || !editable;
  const mailto = `mailto:${m.recipients.map((r) => r.email).filter(Boolean).join(',')}?subject=${encodeURIComponent(m.subject)}&body=${encodeURIComponent(m.body)}`;
  const sendErr = send.error instanceof ApiError && ['resend_confirmation_required', 'email_not_configured'].includes(send.error.code) ? null : send.error;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/relatorios?tab=mensagens">← Mensagens</Link>
            {m.executionId && (
              <>
                {' · '}
                <Link to={`/execucoes/${m.executionId}`}>execução</Link>
              </>
            )}
            {m.appointmentId && (
              <>
                {' · '}
                <Link to={`/agenda/${m.appointmentId}`}>compromisso</Link>
              </>
            )}
            {m.contractId && (
              <>
                {' · '}
                <Link to={`/bancos-de-horas/${m.contractId}`}>banco de horas</Link>
              </>
            )}
          </p>
          <h1>{PURPOSE_LABEL[m.purpose]}</h1>
          <p>
            <MessageStatusBadge status={m.status} /> {m.statusReason && <span className="small muted">{m.statusReason}</span>}
          </p>
        </div>
      </div>
      <div className="alert alert-info small">Nada é enviado automaticamente. Revise destinatários, conteúdo e anexos e confirme o envio manualmente.</div>
      {m.lastSentAt && (
        <div className="alert alert-warning small">
          Já {m.status === 'preparado_aberto' ? 'aberta no aplicativo' : 'enviada'} {m.sendCount}× — último em {dateTime(m.lastSentAt)}.{' '}
          {m.lastSentHash !== m.contentHash ? 'O conteúdo atual é diferente do último envio.' : 'O conteúdo atual é o mesmo do último envio.'}
        </div>
      )}

      <div className="grid grid-2">
        <section className="card form">
          <h2>1. Destinatários e conteúdo</h2>
          <div className="segmented" role="group" aria-label="Canal">
            <button type="button" aria-pressed={form.channel === 'email'} disabled={!editable} onClick={() => set('channel', 'email')}>
              E-mail
            </button>
            <button type="button" aria-pressed={form.channel === 'whatsapp'} disabled={!editable} onClick={() => set('channel', 'whatsapp')}>
              WhatsApp
            </button>
          </div>
          <fieldset disabled={!editable}>
            <legend>Destinatários</legend>
            {m.availableContacts.length === 0 && <p className="small muted">O cliente não tem contatos ativos. Cadastre em Cadastros › Clientes.</p>}
            <div className="checks" style={{ flexDirection: 'column' }}>
              {m.availableContacts.map((c) => {
                const address = form.channel === 'email' ? c.email : c.whatsapp;
                return (
                  <label key={c.id} className="check">
                    <input
                      type="checkbox"
                      checked={form.recipientIds.includes(c.id)}
                      onChange={(e) => set('recipientIds', e.target.checked ? [...form.recipientIds, c.id] : form.recipientIds.filter((x) => x !== c.id))}
                    />
                    {c.name} <span className="small muted">{address ?? `sem ${form.channel === 'email' ? 'e-mail' : 'WhatsApp'}`}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          <label htmlFor="msg-subject" style={{ fontWeight: 600 }}>
            Assunto
          </label>
          <input id="msg-subject" value={form.subject} disabled={!editable} onChange={(e) => set('subject', e.target.value)} />
          <label htmlFor="msg-body" style={{ fontWeight: 600 }}>
            Mensagem (versão em texto)
          </label>
          <textarea id="msg-body" rows={12} value={form.body} disabled={!editable} onChange={(e) => set('body', e.target.value)} />
          <ErrorAlert error={save.error} />
          {editable && (
            <div className="actions">
              <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                Salvar alterações
              </button>
              {dirty && <span className="small muted" style={{ alignSelf: 'center' }}>Salve para atualizar a prévia.</span>}
            </div>
          )}
          {m.attachments.length > 0 && (
            <>
              <h3>Anexos</h3>
              <div className="list">
                {m.attachments.map((a) => (
                  <div key={a.snapshotId} className="list-item">
                    <div className="title">{a.label}</div>
                    <div className="actions" style={{ marginTop: 4 }}>
                      <a className="btn btn-sm" href={`/api/v1/report-snapshots/${a.snapshotId}/pdf`} target="_blank" rel="noopener">
                        Ver PDF
                      </a>
                      <a className="btn btn-sm btn-ghost" href={`/api/v1/report-snapshots/${a.snapshotId}/html`} target="_blank" rel="noopener">
                        Ver HTML
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              {editable && (
                <>
                  <button className="btn btn-sm" disabled={refreshAttachments.isPending} onClick={() => refreshAttachments.mutate()}>
                    Gerar nova versão do anexo (se a origem mudou)
                  </button>
                  {refreshAttachments.data && <span className="small muted">{refreshAttachments.data.changed ? 'Nova versão gerada: confira novamente.' : 'A origem não mudou; versão mantida.'}</span>}
                </>
              )}
            </>
          )}
        </section>

        <section className="card">
          <div className="card-head">
            <h2>2. Prévia</h2>
            <div className="segmented" role="group" aria-label="Dispositivo">
              <button aria-pressed={device === 'desktop'} onClick={() => setDevice('desktop')}>
                Computador
              </button>
              <button aria-pressed={device === 'mobile'} onClick={() => setDevice('mobile')}>
                Celular
              </button>
            </div>
          </div>
          {form.channel === 'email' ? (
            <iframe
              title="Prévia do e-mail"
              sandbox=""
              srcDoc={m.preview.html}
              style={{ width: '100%', maxWidth: device === 'mobile' ? 360 : 640, height: 520, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}
            />
          ) : (
            <pre className="source-preview" style={{ maxWidth: 360 }}>
              {m.body}
            </pre>
          )}
          <p className="small muted">Assunto: {m.subject}</p>
        </section>
      </div>

      <section className="card">
        <h2>3. Conferir e confirmar</h2>
        {m.problems.length > 0 && (
          <div className="alert alert-error">
            <strong>Corrija antes de enviar</strong>
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              {m.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )}
        <dl className="props">
          <dt>Destinatários</dt>
          <dd>{m.recipients.map((r) => `${r.name} <${(m.channel === 'email' ? r.email : r.whatsapp) ?? '—'}>`).join('; ') || '—'}</dd>
          <dt>Canal</dt>
          <dd>{m.channel === 'email' ? `E-mail ${integrations.data?.email.configured ? `(${integrations.data.email.simulator ? 'SIMULADOR de testes' : integrations.data.email.provider})` : '(serviço não configurado em Configurações › Integrações)'}` : m.whatsappMode === 'cloud_api' ? 'WhatsApp pela API oficial (“aceito” não comprova entrega; o PDF não é anexado — envie o link ou o arquivo por outro meio)' : 'WhatsApp por link (abre o aplicativo; entrega não comprovada)'}</dd>
          <dt>Anexos</dt>
          <dd>{m.attachments.map((a) => a.label).join(', ') || '—'}</dd>
        </dl>
        <label className="check" style={{ marginTop: '0.5rem' }}>
          <input type="checkbox" checked={reviewed} disabled={blocking} onChange={(e) => setReviewed(e.target.checked)} />
          Conferi os destinatários, o conteúdo e os anexos desta versão.
        </label>
        <ErrorAlert error={sendErr ?? opened.error} title="Não enviado." />
        {send.data && <div className="alert alert-success">{send.data.simulator ? 'Aceita pelo SIMULADOR de testes (nada real foi enviado).' : 'Aceita pelo provedor. Aceitação não comprova entrega nem leitura.'}</div>}
        <div className="actions" style={{ marginTop: '0.5rem' }}>
          {m.channel === 'email' || m.whatsappMode === 'cloud_api' ? (
            <button className="btn btn-primary" disabled={!reviewed || blocking || send.isPending} onClick={() => send.mutate(false)}>
              {send.isPending ? 'Enviando…' : 'Confirmar e enviar'}
            </button>
          ) : (
            m.whatsappLinks.map((w) => (
              <a
                key={w.url}
                className={`btn btn-primary${!reviewed || blocking ? ' disabled' : ''}`}
                aria-disabled={!reviewed || blocking}
                href={!reviewed || blocking ? undefined : w.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (!reviewed || blocking) return e.preventDefault();
                  opened.mutate('whatsapp');
                }}
              >
                Abrir WhatsApp — {w.name}
              </a>
            ))
          )}
        </div>
        {(notConfigured || (m.channel === 'email' && integrations.data && !integrations.data.email.configured)) && (
          <div className="alert alert-warning" style={{ marginTop: '0.75rem' }}>
            <strong>Serviço de e-mail não configurado — nada foi enviado pelo aplicativo</strong>
            Alternativas: baixe o PDF do anexo, copie o texto ou abra a mensagem no seu aplicativo de e-mail (registrada como “preparada/aberta”, sem comprovação de entrega).
            <div className="actions" style={{ marginTop: '0.5rem' }}>
              <button
                className="btn btn-sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(`${m.subject}\n\n${m.body}`);
                  setCopied(true);
                }}
              >
                {copied ? 'Texto copiado' : 'Copiar texto'}
              </button>
              <a
                className="btn btn-sm"
                href={!reviewed || blocking ? undefined : mailto}
                aria-disabled={!reviewed || blocking}
                onClick={(e) => {
                  if (!reviewed || blocking) return e.preventDefault();
                  opened.mutate('email_app');
                }}
              >
                Abrir no meu e-mail
              </a>
            </div>
          </div>
        )}
      </section>

      {m.attempts.length > 0 && (
        <section className="card">
          <h2>Histórico de tentativas</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Canal</th>
                <th>Resultado</th>
                <th>Destinatários</th>
              </tr>
            </thead>
            <tbody>
              {m.attempts.map((a) => (
                <tr key={a.id}>
                  <td>{dateTime(a.createdAt)}</td>
                  <td>{a.provider}</td>
                  <td>
                    {a.status}
                    {a.error && <div className="small muted">{a.error}</div>}
                  </td>
                  <td className="small">{a.recipients.map((r) => r.name).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {resend && (
        <Dialog title="Confirmar reenvio" onClose={() => setResend(null)}>
          <p>
            Esta mensagem já foi enviada {resend.sendCount}× (último envio em {dateTime(resend.lastSentAt)}).{' '}
            {resend.sameContent ? 'O conteúdo é o mesmo do último envio.' : 'O conteúdo mudou desde o último envio.'}
          </p>
          <div className="actions">
            <button className="btn btn-primary" disabled={send.isPending} onClick={() => send.mutate(true)}>
              Reenviar
            </button>
            <button className="btn" onClick={() => setResend(null)}>
              Cancelar
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
